<template>
	<div class="scoreboard-control">
		<template v-if="supportsGameControl">
			<IceHockeyGameControl
				v-if="isIceHockey || isFloorball || isStreetHockey"
				:variant="isMobileWebApp ? 'mobile' : 'default'"
				:position="position"
				:tags="tags"
				:event="event"
				:stop-clock-on-action="isIceHockey"
				@score-increase="handleScoreIncrease"
				@score-decrease="handleScoreDecrease"
				@clock-start="handleClockStart"
				@clock-stop="handleClockStop"
				@clock-set="handleClockSet"
			/>
			<GameControl
				v-else
				:variant="isMobileWebApp ? 'mobile' : 'default'"
				:position="position"
				:tags="tags"
				:event="event"
				@score-increase="handleScoreIncrease"
				@score-decrease="handleScoreDecrease"
				@clock-start="handleClockStart"
				@clock-stop="handleClockStop"
				@clock-set="handleClockSet"
			/>
			<!-- divider -->
			<div
				v-if="supportsGameControl"
				class="scoreboard-control__divider"
			/>
		</template>
		<!-- scoreboard toggle -->
		<div
			v-if="supportsGameControl"
			class="scoreboard-control__divider"
		/>
		<!-- scoreboard toggle -->
		<div class="scoreboard-control__field">
			<SwitchInput v-model="showScoreboardScoreProxy">
				<template #pre>
					<div class="scoreboard-control__label">
						{{ showScoreboardScoreLabel }}
					</div>
				</template>
			</SwitchInput>
			<template v-if="showScoreboardScoreProxy">
				<br>
				<SwitchInput v-model="showScoreboardTimeProxy">
					<template #pre>
						<div class="scoreboard-control__label">
							{{ showScoreboardTimeLabel }}
						</div>
					</template>
				</SwitchInput>
				<br>
				<template v-if="periodStartMinuteValues.length > 0 && !isUltimateFrisbee">
					<div class="scoreboard-control__divider" />
					<br>
					<div class="scoreboard-control__label">
						{{ periodTimesLabel }}
					</div>
					<br>
					<div class="scoreboard-control__halftime--times">
						<FormField
							v-for="(periodTime, i) in periodStartMinuteValues"
							:key="i"
							:label="`${i + 1}. ${periodTypeLabel}`"
						>
							<FormInput
								:id="`period_time_${i}`"
								:value="periodTime"
								type="number"
								@change="handlePeriodTimeChange($event, i)"
							/>
						</FormField>
					</div>
				</template>
			</template>
		</div>
		<!-- divider -->
		<div class="scoreboard-control__divider" />
		<!-- custom text toggle -->
		<div class="scoreboard-control__field">
			<SwitchInput v-model="customTextEnabledProxy">
				<template #pre>
					<div class="scoreboard-control__label">
						{{ customTextLabel }}
					</div>
				</template>
			</SwitchInput>
			<template v-if="customTextEnabledProxy">
				<br>
				<FormInput
					v-model="customTextInputValue"
				/>
				<br v-if="customTextInputValueHasChanged">
				<Button
					v-if="customTextInputValueHasChanged"
					:text="$Helpers.ucfirst($I18n.trans('commons.save'))"
					theme="dark"
					@click="handleCustomTextChange"
				/>
			</template>
		</div>
	</div>
</template>

<script>
import FormInput from '@videotools/js/one/components/Form/FormInput';
import FormField from '@videotools/js/one/components/Form/FormField';
import SwitchInput from '@components/Switch';
import { GameControl, IceHockeyGameControl } from '@components/GameControl';
import { mapGetters } from 'vuex';
import Button from '@shared/components/Button';

import { GAME_CONTROL_SPORTS, GAME_SPORT } from '@shared/constants/game';

export default {
	name:       'ScoreboardControl',
	components: { FormField, SwitchInput, GameControl, FormInput, IceHockeyGameControl, Button },
	props:      {
		position: {
			type:     Number,
			required: true,
		},
		event: {
			type:     Object,
			required: true,
		},
		tags: {
			type:     Array,
			required: true,
		},
		showScoreboardScoreLabel: {
			type:    String,
			default: 'Show scoreboard in player',
		},
		showScoreboardTimeLabel: {
			type:    String,
			default: 'Show time',
		},
		showScoreboardScore: {
			type:     Boolean,
			required: true,
		},
		showScoreboardTime: {
			type:     Boolean,
			required: true,
		},
		periodStartMinutes: {
			type:    Array,
			default: () => [],
		},
		customText: {
			type:     String,
			required: true,
		},
		customTextEnabled: {
			type:     Boolean,
			required: true,
		},
	},
	data() {
		return {
			customTextInputValue:    '',
			periodStartMinuteValues: [],
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp: 'app/isMobileWebApp',
		}),
		supportsGameControl() {
			return GAME_CONTROL_SPORTS.includes(this.event.sport.name);
		},
		showScoreboardScoreProxy: {
			get() {
				return this.showScoreboardScore;
			},
			set(value) {
				// Toggle both on enable/disable
				this.$emit('set-scoreboard-settings', { showScore: value, showTime: value });
			},
		},
		showScoreboardTimeProxy: {
			get() {
				return this.showScoreboardTime;
			},
			set(value) {
				this.$emit('set-scoreboard-settings', { showScore: this.showScoreboardScore, showTime: value });
			},
		},
		isIceHockey() {
			return this.event.sport.name === GAME_SPORT.ICE_HOCKEY;
		},
		isStreetHockey() {
			return this.event.sport.name === GAME_SPORT.STREET_HOCKEY;
		},
		isFloorball() {
			return this.event.sport.name === GAME_SPORT.FLOORBALL;
		},
		isUltimateFrisbee() {
			return this.event.sport.name === GAME_SPORT.ULTIMATE_FRISBEE;
		},
		customTextLabel() {
			return this.$I18n.trans('one.custom_text');
		},
		periodTypeLabel() {
			return this.periodStartMinutes.length > 2
				? this.$I18n.trans('commons.third')
				: this.$I18n.trans('commons.halftime');
		},
		periodTimesLabel() {
			return `${this.periodTypeLabel} ${this.$I18n.trans('event.attributes.start_time')}`;
		},
		customTextEnabledProxy: {
			get() {
				return this.customTextEnabled;
			},
			set(value) {
				this.$emit('set-custom-text-enabled', value);
			},
		},
		customTextInputValueHasChanged() {
			return this.customTextInputValue !== this.customText;
		},
	},
	watch: {
		periodStartMinutes(value) {
			this.periodStartMinuteValues = value;
		},
		customText(value) {
			this.customTextInputValue = value;
		},
	},
	mounted() {
		this.periodStartMinuteValues = this.periodStartMinutes;
		if (this.customText) {
			this.customTextInputValue = this.customText;
		} else {
			this.customTextInputValue = this.$I18n.trans('one.custom_text_default');
		}
	},
	methods: {
		handleScoreIncrease(...args) {
			this.$emit('score-increase', ...args);
		},
		handleScoreDecrease(...args) {
			this.$emit('score-decrease', ...args);
		},
		handleClockStart(...args) {
			this.$emit('clock-start', ...args);
		},
		handleClockStop(...args) {
			this.$emit('clock-stop', ...args);
		},
		handleClockSet(...args) {
			this.$emit('clock-set', ...args);
		},
		handleCustomTextChange() {
			this.$emit('set-custom-text', this.customTextInputValue);
		},
		handlePeriodTimeChange(value, i) {
			const newValue = structuredClone(this.periodStartMinuteValues);
			newValue[i] = parseInt(value);

			this.periodStartMinuteValues = newValue;
			this.$emit('set-period-start-minutes', newValue);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.scoreboard-control {
	display: flex;
	flex-direction: column;
	flex-wrap: wrap;
	order: 5;
}

.scoreboard-control__divider {
	width: 100%;
	height: 1px;
	background: var(--color-grey-800);
}

.scoreboard-control__field {
	padding: 24px 0 16px 0;
}

.scoreboard-control__label {
	margin-right: 8px;
	flex: 1;
}

.scoreboard-control__halftime--times {
	display: flex;
	gap: 8px
}
</style>
